/**
 * Plain-text and JSON-Lines formatters for the per-thread chat export.
 *
 * Both formatters are PURE: same input → identical output byte-for-byte.
 * That property lets users diff exports across time (see
 * `kaether_for_web/docs/chat-export.md` → "Format determinism") and
 * makes the cap-enforcement self-test trivial.
 *
 * THREAT MODEL
 *   * `fromDisplayName` resolution is the load-bearing invariant. The
 *     formatter ignores the `fromDisplayName` field on each
 *     `ChatExportRecord` and re-resolves from the `LocalRoster`
 *     passed in. If the userId is missing from the roster, the
 *     formatter renders `"Unknown user"` rather than falling back to
 *     the record-level field. This way a peer who set a malicious
 *     display name (carried by a buggy upstream collector that DID
 *     trust the peer-claimed string) still cannot land that string
 *     in the on-disk file.
 *   * 50_000-message cap enforced at the formatter so even a bug
 *     upstream cannot ship a 5M-message export. Returned as a defined
 *     error rather than silently truncating — the UI must explicitly
 *     reject the export or chunk by date range.
 *   * Sorted keys + sorted records: the JSON Lines output is
 *     byte-for-byte identical for the same input regardless of how
 *     the record array was ordered or how V8's object-iteration
 *     order quirks behave. Diffability across time falls out.
 */

import {
  type ChatExportRecord,
  type LocalRoster,
  UNKNOWN_DISPLAY_NAME,
} from "./ChatExportRecord";

/** Hard cap on a single export. Enforced before any serialisation work. */
export const MAX_MESSAGES_PER_EXPORT = 50_000 as const;

/** Wire schema number for the JSON Lines header. Readers fail-closed on unknown. */
export const CHAT_EXPORT_SCHEMA_VERSION = 1 as const;

/** Options accepted by both formatters. */
export interface ChatExportFormatOptions {
  /** Human-readable channel title — written into the header line. */
  readonly channelTitle: string;
  /**
   * Locally-trusted username book at format time. The ONLY way a
   * display name reaches the file on disk. See `ChatExportRecord.ts`.
   */
  readonly roster: LocalRoster;
  /**
   * Unix milliseconds at export time. Defaulted to `Date.now()` on
   * call but settable so tests can produce deterministic output.
   */
  readonly exportedAt?: number;
}

/**
 * Discriminated result returned by both formatters. The cap-exceeded
 * branch carries the offending count so the UI can render
 * "Export is N messages — pick a smaller window."
 */
export type ChatExportFormatResult =
  | { readonly ok: true; readonly output: string }
  | {
      readonly ok: false;
      readonly error: { readonly kind: "tooMany"; readonly count: number };
    };

/**
 * Sort records ascending by `sentAt`. Stable on tie (preserves caller
 * order for messages with identical timestamps).
 *
 * Returned as a new array — never mutates the caller's input.
 */
function sortBySentAt(records: readonly ChatExportRecord[]): ChatExportRecord[] {
  return [...records].sort((a, b) => a.sentAt - b.sentAt);
}

/**
 * Resolve a userId to a display name strictly via the local roster.
 * Falls back to `UNKNOWN_DISPLAY_NAME` if the roster has no entry —
 * NEVER to a record-level `fromDisplayName`.
 */
function resolveDisplayName(userId: string, roster: LocalRoster): string {
  return roster[userId] ?? UNKNOWN_DISPLAY_NAME;
}

/**
 * Format a Unix-milliseconds timestamp as `YYYY-MM-DD HH:MM:SS` in
 * UTC. Locked to UTC + ISO-derived format so an export shared across
 * timezones renders identically to every reader and so the diff
 * property holds across runs.
 */
function formatTimestamp(unixMillis: number): string {
  const d = new Date(unixMillis);
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  const hh = d.getUTCHours().toString().padStart(2, "0");
  const mi = d.getUTCMinutes().toString().padStart(2, "0");
  const ss = d.getUTCSeconds().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

/**
 * Stable JSON.stringify with alphabetically sorted keys. Used by the
 * JSON Lines formatter for determinism — two encodes of the same
 * input produce byte-for-byte identical output.
 *
 * Only handles the value shapes we actually emit: string, number,
 * boolean, plain object. Arrays and null are not emitted by this
 * formatter today; if they're added later the helper must grow first.
 */
function stableStringify(value: Record<string, unknown>): string {
  const keys = Object.keys(value).sort();
  const parts: string[] = [];
  for (const k of keys) {
    const v = value[k];
    if (v === undefined) continue;
    parts.push(`${JSON.stringify(k)}:${JSON.stringify(v)}`);
  }
  return `{${parts.join(",")}}`;
}

// ---------------------------------------------------------------------------
// Plain-text
// ---------------------------------------------------------------------------

/**
 * Render a chat as a human-readable transcript. Records are sorted by
 * `sentAt` ascending. The output begins with a header line carrying
 * the channel title and export timestamp, followed by one line per
 * message:
 *
 *     # kaether chat export — Channel Title — 2024-01-01 00:00:00 UTC
 *     [2024-01-01 12:00:00] Alice: hello world
 *     [2024-01-01 12:00:30] Bob: hi
 *
 * Attachments render with their `mediaSummary` appended:
 *
 *     [2024-01-01 12:01:00] Alice: [image (1.2 MB)]
 *
 * The plain-text output has NO machine-parse contract; JSON Lines is
 * the round-trip path.
 */
export function formatPlainText(
  records: readonly ChatExportRecord[],
  options: ChatExportFormatOptions,
): ChatExportFormatResult {
  if (records.length > MAX_MESSAGES_PER_EXPORT) {
    return { ok: false, error: { kind: "tooMany", count: records.length } };
  }

  const exportedAt = options.exportedAt ?? Date.now();
  const lines: string[] = [];
  lines.push(
    `# kaether chat export — ${options.channelTitle} — ${formatTimestamp(exportedAt)} UTC`,
  );

  const sorted = sortBySentAt(records);
  for (const r of sorted) {
    const name = resolveDisplayName(r.fromUserId, options.roster);
    const stamp = formatTimestamp(r.sentAt);
    const bodyText =
      r.mediaSummary !== undefined && r.mediaSummary.length > 0
        ? `[${r.mediaSummary}]`
        : r.body;
    lines.push(`[${stamp}] ${name}: ${bodyText}`);
  }
  return { ok: true, output: lines.join("\n") };
}

// ---------------------------------------------------------------------------
// JSON Lines
// ---------------------------------------------------------------------------

/**
 * Render a chat as JSON Lines. Line 1 is a header object; lines 2..N
 * are one record per line. Records are sorted by `sentAt` ascending
 * and EACH OBJECT is serialised with alphabetically sorted keys so
 * the output is byte-for-byte deterministic.
 *
 * Header shape (keys alphabetical when serialised):
 *   { channelTitle, exportedAt, schemaVersion }
 *
 * Record shape (keys alphabetical when serialised; `fromDisplayName`
 * comes from the roster, NOT the record's own field):
 *   { body, fromDisplayName, fromUserId, mediaSummary?, messageId, sentAt }
 *
 * No trailing newline — concat of lines joined with "\n".
 */
export function formatJsonLines(
  records: readonly ChatExportRecord[],
  options: ChatExportFormatOptions,
): ChatExportFormatResult {
  if (records.length > MAX_MESSAGES_PER_EXPORT) {
    return { ok: false, error: { kind: "tooMany", count: records.length } };
  }

  const exportedAt = options.exportedAt ?? Date.now();
  const header = {
    channelTitle: options.channelTitle,
    exportedAt,
    schemaVersion: CHAT_EXPORT_SCHEMA_VERSION,
  };

  const lines: string[] = [];
  lines.push(stableStringify(header as Record<string, unknown>));

  const sorted = sortBySentAt(records);
  for (const r of sorted) {
    const obj: Record<string, unknown> = {
      body: r.body,
      fromDisplayName: resolveDisplayName(r.fromUserId, options.roster),
      fromUserId: r.fromUserId,
      messageId: r.messageId,
      sentAt: r.sentAt,
    };
    if (r.mediaSummary !== undefined) {
      obj.mediaSummary = r.mediaSummary;
    }
    lines.push(stableStringify(obj));
  }

  return { ok: true, output: lines.join("\n") };
}
